import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "moment/locale/az";
import { IoEyeOutline, IoEyeOffOutline, IoClose } from "react-icons/io5";
import { createUser, getUsers } from "../api";
import { getStableDeviceId } from "../utils/deviceId";
import "../styles/sifaris.css";

const WA = {
  dark: "#075E54",
  main: "#128C7E",
  accent: "#25D366",
  bg: "#f0efeeff",
  white: "#FFFFFF",
  text: "#102A26",
  border: "#C8D5CF",
  placeholder: "#7BA89C",
};

const AppModal = ({
  open,
  title = "Diqqət",
  message = "",
  type = "info",
  buttonText = "Bağla",
  onClose,
  actions,
}) => {
  if (!open) return null;

  return (
    <div className="sq-modal-overlay">
      <div className="sq-alert-modal">
        <button className="sq-close-btn" onClick={onClose} type="button">
          <IoClose size={22} />
        </button>

        <h3 className={`sq-alert-title sq-alert-${type}`}>{title}</h3>
        <p className="sq-alert-message">{message}</p>

        {actions?.length ? (
          <div className="sq-alert-actions">
            {actions.map((action, index) => (
              <button
                key={index}
                type="button"
                className={`sq-alert-button ${
                  action.variant === "secondary"
                    ? "sq-alert-button-secondary"
                    : ""
                }`}
                onClick={action.onClick}
              >
                {action.label}
              </button>
            ))}
          </div>
        ) : (
          <button className="sq-alert-button" onClick={onClose} type="button">
            {buttonText}
          </button>
        )}
      </div>
    </div>
  );
};

const RegisterScreen = () => {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: "",
    message: "",
    type: "info",
    buttonText: "Bağla",
    actions: null,
  });

  moment.locale("az");

  const showModal = ({
    title = "Diqqət",
    message = "",
    type = "info",
    buttonText = "Bağla",
    actions = null,
  }) => {
    setModalConfig({ title, message, type, buttonText, actions });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handlePhoneChange = (value) => {
    const cleanedText = value.replace(/[^0-9]/g, "");
    setPhoneNumber(cleanedText);
  };

  const handleRegister = async () => {
    if (name.trim().length <= 2) {
      showModal({
        title: "Diqqət",
        message: "Ad ən az 3 hərf olmalıdır.",
        type: "warning",
      });
      return;
    }

    if (phoneNumber.trim().length !== 10) {
      showModal({
        title: "Diqqət",
        message: "Telefon nömrəsi 10 rəqəm olmalıdır.",
        type: "warning",
      });
      return;
    }

    if (password.length <= 5) {
      showModal({
        title: "Diqqət",
        message: "Şifrə ən az 6 hərf olmalıdır.",
        type: "warning",
      });
      return;
    }

    if (password !== confirmPassword) {
      showModal({
        title: "Diqqət",
        message: "Şifrələr uyğun deyil.",
        type: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const stableDeviceId = await getStableDeviceId();
      localStorage.setItem("deviceUUID", stableDeviceId);

      const usersResponse = await getUsers();
      const users = usersResponse?.data || [];

      const deviceBlocked = users.some(
        (u) => u?.phoneId === stableDeviceId && u?.isDisable === true
      );

      if (deviceBlocked) {
        showModal({
          title: "Diqqət!",
          message: "Bu cihaz bloklanıb. Hesab yaratmaq mümkün deyil.",
          type: "error",
        });
        setLoading(false);
        return;
      }

      const exists = users.some((u) => u.phoneNumber === phoneNumber);

      if (exists) {
        showModal({
          title: "Xəta",
          message: "Bu telefon nömrəsi artıq qeydiyyatdan keçib!",
          type: "error",
        });
        setLoading(false);
        return;
      }

      const todayDate = moment().format("DD.MM.YYYY");
      const todayTime = moment().format("HH:mm");

      const createdCustomer = {
        oneSignal: "",
        name: name.trim(),
        phoneNumber: phoneNumber.trim(),
        notificationHistory: [],
        password: password.trim(),
        orderCount: 0,
        online: false,
        courierStatus: "",
        createdDate: `${todayDate} | ${todayTime}`,
        isDisable: false,
        phoneId: stableDeviceId,
        isVerifyPhone: true,
      };

      await createUser(createdCustomer);

      showModal({
        title: "Qeydiyyat tamamlandı!",
        message: "Qeydiyyatdan keçdiyiniz əlaqə məlumatları ilə daxil olun.",
        type: "success",
        actions: [
          {
            label: "Giriş",
            onClick: () => {
              closeModal();
              navigate("/sifaris/login");
            },
          },
        ],
      });
    } catch (error) {
      console.error("Qeydiyyat zamanı xəta:", error);
      showModal({
        title: "Xəta",
        message: "Bağlantı xətası. Yenidən cəhd edin.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        className="sq-page"
        style={{
          backgroundColor: WA.bg,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        
        <div className="sq-card">
          <h1 className="sq-title">Qeydiyyat</h1>

          <input
            className="sq-input"
            placeholder="Tam Ad"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={30}
          />

          <input
            className="sq-input"
            placeholder="Telefon nömrəsi: 055XXXXXXXX"
            value={phoneNumber}
            onChange={(e) => handlePhoneChange(e.target.value)}
            maxLength={10}
            inputMode="numeric"
          />

          <div className="sq-password-wrap">
            <input
              className="sq-input sq-password-input"
              placeholder="Şifrə Daxil Edin"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              maxLength={25}
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="sq-eye-btn"
              aria-label={showPassword ? "Şifrəni gizlət" : "Şifrəni göstər"}
            >
              {showPassword ? (
                <IoEyeOffOutline size={22} color="#666" />
              ) : (
                <IoEyeOutline size={22} color="#666" />
              )}
            </button>
          </div>

          <div className="sq-password-wrap">
            <input
              className="sq-input sq-password-input"
              placeholder="Şifrəni Yenidən Daxil Edin"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              maxLength={25}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              className="sq-eye-btn"
              aria-label={
                showConfirmPassword ? "Şifrəni gizlət" : "Şifrəni göstər"
              }
            >
              {showConfirmPassword ? (
                <IoEyeOffOutline size={22} color="#666" />
              ) : (
                <IoEyeOutline size={22} color="#666" />
              )}
            </button>
          </div>

          <button
            className="sq-primary-btn"
            onClick={handleRegister}
            disabled={loading}
            type="button"
          >
            {loading ? "Yüklənir..." : "Qeydiyyat"}
          </button>

          <button
            type="button"
            className="sq-register-link"
            onClick={() => navigate("/sifaris/login")}
          >
            Artıq hesabınız var? Daxil olun.
          </button>
        </div>
      </div>

      <AppModal
        open={modalOpen}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
        buttonText={modalConfig.buttonText}
        actions={modalConfig.actions}
        onClose={closeModal}
      />
    </>
  );
};

export default RegisterScreen;